/**
 * UI-side permission helpers. The RULES come from @tekansa/core/permissions, the same file the
 * API enforces; the role's matrix (module × action, scope) comes with the session (/auth/me),
 * so buttons shown always match what the server allows. The API enforces them regardless.
 */
import {
  BUILTIN_ROLE_DESCRIPTIONS,
  BUILTIN_ROLE_LABELS,
  can,
  scopeOf,
  type Module,
  type Permission,
  type Role,
  type Scope,
} from '@tekansa/core/permissions';
import { useSession } from './use-session';

export type { Role };

/** Can the current person do this in the active company? */
export function useCan(permission: Permission): boolean {
  const { matrix } = useSession();
  return matrix !== null && can({ matrix, kind: 'session' }, permission);
}

/** A checker for several permissions at once (e.g. which sidebar items to show). */
export function useAllowed(): (permission: Permission) => boolean {
  const { matrix } = useSession();
  return (permission) => matrix !== null && can({ matrix, kind: 'session' }, permission);
}

/** All / Team / Own for a module, or null without access. */
export function useScope(module: Module): Scope | null {
  const { matrix } = useSession();
  return matrix ? scopeOf(matrix, module) : null;
}

/** The label of a built-in role (custom roles carry their own name). */
export const roleLabel = (role: Role, customName?: string | null): string =>
  role === 'custom' ? (customName ?? 'Custom role') : BUILTIN_ROLE_LABELS[role];

export const roleDescription = (role: Role): string =>
  role === 'custom' ? 'A role made for this company' : BUILTIN_ROLE_DESCRIPTIONS[role];
